import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Box } from "@mui/material";
import AutoComplete from "components/basic/autoComplete";
import Button from "components/basic/button";
import DatePicker from "components/basic/datePicker";
import FieldError from "components/basic/fieldError";
import TextField from "components/basic/textField";
import Typography from "components/basic/typography";
import UploadFile from "components/basic/uploadFile";
import { useFieldErrors } from "hooks/useFieldErrors";
import { useMaskedInput } from "hooks/useMaskedInput";
import { useS3 } from "hooks/useS3";
import { provinces, provinceLabel } from "pages/calculator/data";
import {
  ELECTRICAL_LICENSE_NUMBER_PLACEHOLDER,
  formatElectricalLicenseNumber,
  formatIsoDate,
  todayIsoDate,
} from "pages/formTools";
import { Vendor } from "pages/userAuthentication/signUp";
import DocumentCard from "../DocumentCard";
import { InstallationDraft, validateInstallation } from "../../helpers";

// `label` is the stored value; `labelKey` is what the user reads.
const YES_NO = [
  { id: 1, label: "Yes", labelKey: "yes" },
  { id: 2, label: "No", labelKey: "no" },
] as const;

const toDraft = (vendor: Vendor): InstallationDraft => ({
  offeringEvInstallation: vendor.offeringEvInstallation,
  province: vendor.province,
  licenseFile: null,
  licenseUrl: vendor.electricalLicenseURL,
  licenseNumber: vendor.electricalLicenseNumber ?? "",
  licenseExpiryDate: vendor.electricalLicenseExpiryDate,
});

interface InstallationSectionProps {
  vendor: Vendor;
  onSave: (updates: Record<string, string | null>) => Promise<boolean>;
}

/** Whether the vendor installs chargers, and the licence that allows it. */
const InstallationSection: React.FC<InstallationSectionProps> = ({
  vendor,
  onSave,
}) => {
  const { uploadToS3 } = useS3();
  const { t } = useTranslation("profile");
  const { t: tAccount } = useTranslation("account");
  const { t: tCommon } = useTranslation();
  const yesNoLabel = (value: string) => {
    const option = YES_NO.find(({ label }) => label === value);
    return option ? tAccount(option.labelKey) : value;
  };
  const provinceName = (name: string) => {
    const province = provinces.find((p) => p.name === name);
    return province ? provinceLabel(tCommon, province.code) : name;
  };
  const installs = vendor.offeringEvInstallation === "Yes";
  // Installers who registered before the expiry date was collected must add it.
  const [editing, setEditing] = useState(
    installs && !vendor.electricalLicenseExpiryDate,
  );
  const [draft, setDraft] = useState(() => toDraft(vendor));
  const [saving, setSaving] = useState(false);
  const { errors, validateAll, revalidate } =
    useFieldErrors(validateInstallation);
  const handleNumberChange = useMaskedInput(formatElectricalLicenseNumber);

  const update = (patch: Partial<InstallationDraft>) => {
    const next = { ...draft, ...patch };
    setDraft(next);
    revalidate(next);
  };

  const handleSave = async () => {
    if (!validateAll(draft)) {
      return;
    }

    setSaving(true);
    const offers = draft.offeringEvInstallation === "Yes";
    const url =
      offers && draft.licenseFile
        ? await uploadToS3(draft.licenseFile, "electrical-licenses")
        : draft.licenseUrl;
    if (offers && !url) {
      setSaving(false);
      return;
    }
    const saved = await onSave({
      offeringEvInstallation: draft.offeringEvInstallation,
      province: draft.province,
      ...(offers
        ? {
            electricalLicenseURL: url,
            electricalLicenseNumber: draft.licenseNumber,
            electricalLicenseExpiryDate: draft.licenseExpiryDate,
          }
        : {}),
    });
    setSaving(false);
    if (saved) {
      setEditing(false);
    }
  };

  const offers = draft.offeringEvInstallation === "Yes";

  return (
    <DocumentCard
      title={t("installation.title")}
      rows={[
        {
          label: t("installation.offering"),
          value: yesNoLabel(vendor.offeringEvInstallation),
        },
        {
          label: t("installation.province"),
          value: provinceName(vendor.province),
        },
        ...(installs
          ? [
              {
                label: t("installation.license_number"),
                value: vendor.electricalLicenseNumber ?? "",
              },
              {
                label: t("installation.license_expiry"),
                value:
                  formatIsoDate(vendor.electricalLicenseExpiryDate) ||
                  t("documents.not_set"),
              },
            ]
          : []),
      ]}
      documentUrl={installs ? vendor.electricalLicenseURL : null}
      editing={editing}
      onEdit={() => {
        setDraft(toDraft(vendor));
        setEditing(true);
      }}
    >
      <Typography variant="body2" style={{ opacity: 0.7 }}>
        {t("installation.hint")}
      </Typography>

      <Typography variant="body2">{t("installation.question")}</Typography>
      <AutoComplete
        name="offeringEvInstallation"
        value={
          YES_NO.find((o) => o.label === draft.offeringEvInstallation)?.id ??
          null
        }
        options={YES_NO.map(({ id, labelKey }) => ({
          id,
          label: tAccount(labelKey),
        }))}
        label={t("installation.select")}
        onChange={(value) =>
          update({
            offeringEvInstallation:
              YES_NO.find(({ id }) => id === value?.id)?.label ?? "",
          })
        }
        error={errors.offeringEvInstallation ?? undefined}
      />

      <Typography variant="body2">{t("installation.province")}</Typography>
      <AutoComplete
        name="province"
        value={provinces.find((p) => p.name === draft.province)?.id ?? null}
        options={provinces.map(({ id, code }) => ({
          id,
          label: provinceLabel(tCommon, code),
        }))}
        label={t("installation.select")}
        onChange={(value) =>
          update({
            province: provinces.find(({ id }) => id === value?.id)?.name ?? "",
          })
        }
        error={errors.province ?? undefined}
      />

      {offers && (
        <>
          <UploadFile
            label={t("installation.upload")}
            onChange={(licenseFile) => update({ licenseFile })}
            file={draft.licenseFile}
          />
          <FieldError error={errors.licenseFile} />

          <Typography variant="body2">
            {t("installation.license_number_label")}
          </Typography>
          <TextField
            name="electricalLicenseNumber"
            placeHolder={ELECTRICAL_LICENSE_NUMBER_PLACEHOLDER}
            value={draft.licenseNumber}
            onChange={(event) =>
              update({
                licenseNumber: handleNumberChange(event, draft.licenseNumber),
              })
            }
            error={errors.licenseNumber ?? undefined}
          />

          <Typography variant="body2">
            {t("installation.license_expiry_label")}
          </Typography>
          <DatePicker
            value={draft.licenseExpiryDate}
            minDate={todayIsoDate()}
            onChange={(licenseExpiryDate) => update({ licenseExpiryDate })}
            error={errors.licenseExpiryDate ?? undefined}
          />
        </>
      )}

      <Box display="flex" gap={1} justifyContent="flex-end">
        <Button variant="text" onClick={() => setEditing(false)}>
          {t("cancel")}
        </Button>
        <Button
          variant="contained"
          color="warning"
          style={{ color: "white" }}
          onClick={handleSave}
          loading={saving}
          disabled={saving}
        >
          {t("save")}
        </Button>
      </Box>
    </DocumentCard>
  );
};

export default InstallationSection;
